import type {
  CreateOperatorUserDto,
  UpdateOperatorUserDto,
} from '@data-delivery/permission-ui-api';
import { operatorRolesApi } from '@data-delivery/permission-ui-api';
import { useMutation, useQuery } from '@tanstack/react-query';
import { permissionQueryClient as qc } from '../query-client';

export const operatorUserKeys = {
  all: ['operator-users'] as const,
  list: () => [...operatorUserKeys.all, 'list'] as const,
  detail: (id: string) => [...operatorUserKeys.all, 'detail', id] as const,
};

type OperatorUserEntity = NonNullable<
  Awaited<ReturnType<typeof operatorRolesApi.getAllOperatorUsers>>['data']
>[number];

export function useOperatorUsersQuery() {
  return useQuery({
    queryKey: operatorUserKeys.list(),
    async queryFn() {
      const { data } = await operatorRolesApi.getAllOperatorUsers();
      return data ?? [];
    },
  });
}

export function useOperatorUserQuery(id: string | undefined) {
  return useQuery({
    queryKey: id ? operatorUserKeys.detail(id) : ['operator-users', 'detail', 'none'],
    enabled: Boolean(id),
    async queryFn() {
      if (!id) {
        return undefined;
      }

      const { data } = await operatorRolesApi.getOperatorUserById({ id });
      return data ?? undefined;
    },
  });
}

export function useCreateOperatorUserMutation() {
  return useMutation<
    OperatorUserEntity | undefined,
    unknown,
    CreateOperatorUserDto,
    { previousList: OperatorUserEntity[]; optimisticId: string }
  >({
    mutationKey: [...operatorUserKeys.all, 'create'],
    async mutationFn(dto) {
      const { data } = await operatorRolesApi.createAOperatorUser({ createOperatorUserDto: dto });
      return data ?? undefined;
    },
    async onMutate(dto) {
      await qc.cancelQueries({ queryKey: operatorUserKeys.list() });
      const previousList = qc.getQueryData<OperatorUserEntity[]>(operatorUserKeys.list()) || [];
      const optimistic: OperatorUserEntity = {
        id: `temp-${Date.now()}`,
        name: dto.name,
        email: dto.email,
        isSystemUser: dto.isSystemUser ?? false,
        clientId: dto.clientId,
        businessGroupRoles: dto.businessGroupRoles ?? [],
        lastModified: Date.now(),
      } satisfies OperatorUserEntity;
      qc.setQueryData(operatorUserKeys.list(), [...previousList, optimistic]);
      return { previousList, optimisticId: optimistic.id };
    },
    onError(_error, _dto, context) {
      if (context?.previousList) {
        qc.setQueryData(operatorUserKeys.list(), context.previousList);
      }
    },
    onSuccess(serverEntity, _dto, context) {
      if (!serverEntity || !context?.optimisticId) {
        return;
      }

      const list = qc.getQueryData<OperatorUserEntity[]>(operatorUserKeys.list()) || [];
      qc.setQueryData(
        operatorUserKeys.list(),
        list.map(u => (u.id === context.optimisticId ? serverEntity : u)),
      );
    },
    onSettled() {
      qc.invalidateQueries({ queryKey: operatorUserKeys.list() });
    },
  });
}

function resolveClientId(
  current: OperatorUserEntity,
  dto: { isSystemUser?: boolean; clientId?: string },
) {
  const isSystemUser = dto.isSystemUser ?? current.isSystemUser ?? false;
  return isSystemUser ? dto.clientId ?? current.clientId : undefined;
}

export function useUpdateOperatorUserMutation(id: string) {
  type UpdateInput = {
    lastModified: number;
    name?: string;
    email?: string;
    isSystemUser?: boolean;
    clientId?: string;
    businessGroupRoles?: UpdateOperatorUserDto['businessGroupRoles'];
  };
  return useMutation<
    OperatorUserEntity | undefined,
    unknown,
    UpdateInput,
    { previousList: OperatorUserEntity[]; previousDetail?: OperatorUserEntity }
  >({
    mutationKey: [...operatorUserKeys.all, 'update', id],
    async mutationFn(dto) {
      const payload: UpdateOperatorUserDto = { lastModified: dto.lastModified };
      if (dto.name !== undefined) {
        payload.name = dto.name;
      }

      if (dto.email !== undefined) {
        payload.email = dto.email;
      }

      if (dto.isSystemUser !== undefined) {
        payload.isSystemUser = dto.isSystemUser;
      }

      if (dto.clientId !== undefined) {
        payload.clientId = dto.clientId;
      }

      if (dto.businessGroupRoles !== undefined) {
        payload.businessGroupRoles = dto.businessGroupRoles;
      }

      const { data } = await operatorRolesApi.updateOperatorUser({
        id,
        updateOperatorUserDto: payload,
      });
      return data ?? undefined;
    },
    async onMutate(dto) {
      await qc.cancelQueries({ queryKey: operatorUserKeys.list() });
      await qc.cancelQueries({ queryKey: operatorUserKeys.detail(id) });
      const previousList = qc.getQueryData<OperatorUserEntity[]>(operatorUserKeys.list()) || [];
      const previousDetail = qc.getQueryData<OperatorUserEntity>(operatorUserKeys.detail(id));
      const nextList = previousList.map(u => {
        if (u.id !== id) {
          return u;
        }

        return {
          ...u,
          name: dto.name === undefined ? u.name : dto.name,
          email: dto.email === undefined ? u.email : dto.email,
          isSystemUser: dto.isSystemUser === undefined ? u.isSystemUser : dto.isSystemUser,
          clientId: resolveClientId(u, dto),
          businessGroupRoles:
            dto.businessGroupRoles === undefined ? u.businessGroupRoles : dto.businessGroupRoles,
          lastModified: dto.lastModified,
        };
      });
      qc.setQueryData(operatorUserKeys.list(), nextList);
      if (previousDetail) {
        qc.setQueryData(operatorUserKeys.detail(id), {
          ...previousDetail,
          name: dto.name === undefined ? previousDetail.name : dto.name,
          email: dto.email === undefined ? previousDetail.email : dto.email,
          isSystemUser:
            dto.isSystemUser === undefined ? previousDetail.isSystemUser : dto.isSystemUser,
          clientId: resolveClientId(previousDetail, dto),
          businessGroupRoles:
            dto.businessGroupRoles === undefined
              ? previousDetail.businessGroupRoles
              : dto.businessGroupRoles,
          lastModified: dto.lastModified,
        });
      }

      return { previousList, previousDetail };
    },
    onError(_error, _dto, context) {
      if (context?.previousList) {
        qc.setQueryData(operatorUserKeys.list(), context.previousList);
      }

      if (context?.previousDetail) {
        qc.setQueryData(operatorUserKeys.detail(id), context.previousDetail);
      }
    },
    onSuccess(serverEntity) {
      if (!serverEntity) {
        return;
      }

      const list = qc.getQueryData<OperatorUserEntity[]>(operatorUserKeys.list()) || [];
      qc.setQueryData(
        operatorUserKeys.list(),
        list.map(u => (u.id === id ? { ...u, ...serverEntity } : u)),
      );
      const detail = qc.getQueryData<OperatorUserEntity>(operatorUserKeys.detail(id));
      if (detail) {
        qc.setQueryData(operatorUserKeys.detail(id), { ...detail, ...serverEntity });
      }
    },
    onSettled() {
      qc.invalidateQueries({ queryKey: operatorUserKeys.detail(id) });
      qc.invalidateQueries({ queryKey: operatorUserKeys.list() });
    },
  });
}

export function useDeleteOperatorUserMutation() {
  return useMutation<unknown, unknown, string, { previousList: OperatorUserEntity[] }>({
    mutationKey: [...operatorUserKeys.all, 'delete'],
    async mutationFn(id: string) {
      const { data } = await operatorRolesApi.deleteOperatorUserById({ id });
      return data;
    },
    async onMutate(id: string) {
      await qc.cancelQueries({ queryKey: operatorUserKeys.list() });
      const previousList = qc.getQueryData<OperatorUserEntity[]>(operatorUserKeys.list()) || [];
      qc.setQueryData(
        operatorUserKeys.list(),
        previousList.filter(u => u.id !== id),
      );
      return { previousList };
    },
    onError(_error, _id, context) {
      if (context?.previousList) {
        qc.setQueryData(operatorUserKeys.list(), context.previousList);
      }
    },
    onSettled() {
      qc.invalidateQueries({ queryKey: operatorUserKeys.list() });
    },
  });
}
